import { useState, useEffect, useContext, useRef } from 'react';
import {
  Container, 
  Grid, 
  Typography, 
  Button, 
  Divider, 
  TextField, 
  Paper
} from '@mui/material';
import PostAddIcon from '@mui/icons-material/Add';
import { DataGrid } from '@mui/x-data-grid';
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import AddChartwineDialog from '../components/AddChartwineDialog';
import { fetchWithInterceptor } from '../utils/fetchWithInterceptor';

const PAGE_SIZE = 20;

const WineChart = () => {

  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    country: '',
    region: '',
    subregion: '',
    vintage: '',
    score: '',
  });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [rowCount, setRowCount] = useState(0);
  const active = useRef(true);
  const [rowId, setRowId] = useState(null);
  const [data, setData] = useState([]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const showDialog = () => {
    setDialogVisible(true);
  }

  const hideDialog = () => {
    setDialogVisible(false);
    setRowId(null);
  }

  const handleSaveNewVintage = async (data) => {
    setIsLoading(true);
    const response = await fetchWithInterceptor("/api/vintage-chart", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-access-token": authContext.getToken(),
      },
      body: JSON.stringify(data),
    });

    if (response.ok) {
      const savedData = await response.json();
      
      if (!savedData || typeof savedData.id === "undefined") {
        appContext.showMessage("Error: el servidor no devolvió un ID válido", "error");
      } else {
        setDialogVisible(false);
        setData((prev) => [{ ...savedData, id: savedData.id }, ...prev]);
      }
    } else {
      appContext.showMessage("Error saving vintage", "error");
    }
    setIsLoading(false);
  };

  const handleUpdate = async (data) => {
    setIsLoading(true);
    const response = await fetchWithInterceptor(`/api/vintage-chart/${rowId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
        'x-access-token': authContext.getToken()
      },
    });
    if (response.ok) {
      hideDialog(true);
    } else {
      appContext.showMessage(
        "There was an error updating the vintage chart",
        "error"
      );
    }
    setIsLoading(false);
  };

  const onSave = async (data) => {
    if (rowId) {
      await handleUpdate(data);
    } else {
      await handleSaveNewVintage(data);
    }
    setRowId(null);
  };
  
  const handleRowClick = (params) => {
    setRowId(params.row.id);
    setDialogVisible(true);
  };
  
  const columns = [
    {
      field: 'country',
      headerName: 'Country',
      valueGetter: (value,) => value?.name || "",
      width: 250,
    },
    { field: 'region', headerName: 'Region', width: 300 },
    { field: 'vintage', headerName: 'Vintage', width: 250 },
    { field: 'score', headerName: 'Score', width: 120 },
    { field: 'drink', headerName: 'Recommendation', width: 250 },
  ];

  const loadVintageCharts = async (reset = false) => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    try {
      let where = {};
      if (filters.country) where.country = { name: {contains: filters.country.name } };
      if (filters.region) where.region = { contains: filters.region };
      if (filters.subregion) where.subregion = { contains: filters.subregion };
      if (filters.vintage) where.vintage = { equals: Number(filters.vintage) };
      if (filters.score) where.score = { equals: Number(filters.score) };
      const query = encodeURIComponent(JSON.stringify(where));
      const response = await fetchWithInterceptor(
        `/api/vintage-chart?where=${query}&skip=${reset ? 0 : paginationModel.page * PAGE_SIZE}&take=${PAGE_SIZE}&count=true&orderBy=createdAt:desc`,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-access-token': authContext.getToken(),
          },
        }
      );
      const result = await response.json();
      if (!active.current) return;

      setData(result.vintageCharts);
      setRowCount(result.count || 0);

      if (reset) setPaginationModel({ ...paginationModel, page: 0 });
    } catch (error) {
      appContext.showMessage("Error fetching vintage data", "error");
      console.error("Fetch error:", error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadVintageCharts(false);
    // eslint-disable-next-line
  }, [paginationModel.page]);

  useEffect(() => {
    loadVintageCharts(true);
    // eslint-disable-next-line
  }, [filters]);

  return (
    <>
      <Typography variant="body1" sx={{ ml: 3, mt: 3, mr: 5, mb: 1, color: "text.secondary" }}>
        Manage different product vintages and all their associated technical information. Ensure our database stays current with accurate vintage-specific details for every bottle.
      </Typography>
      <Container  sx={{ pb: 3 }}  component="main" maxWidth="100%" >
      <Paper sx={{ width: '100%', backgroundColor: 'background.paper', mt: 2, mb: 2 }}>
        <div style={{ padding: 16 }}>
          <Typography variant="h6">Search Filters</Typography>
          <br />
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, md: 2 }}>
              <TextField
                label="Country"
                name="country"
                variant="outlined"
                value={filters.country}
                onChange={handleFilterChange}
                fullWidth
              />
            </Grid>
            <Grid  size={{ xs: 6, md: 2 }}>
              <TextField
                label="Region"
                name="region"
                color="primary"
                variant="outlined"
                value={filters.region}
                onChange={handleFilterChange}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <TextField
                label="Vintage"
                name="vintage"
                variant="outlined"
                value={filters.vintage}
                onChange={handleFilterChange}
                fullWidth
                type="number"
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <TextField
                label="Score"
                name="score"
                variant="outlined"
                value={filters.score}
                onChange={handleFilterChange}
                fullWidth
                type="number"
              />
            </Grid>
          </Grid>
          </div>

          <Divider />
        
            <Grid container justifyContent="flex-end" style={{ padding: 16 }}>
              <Grid>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    startIcon={<PostAddIcon />}
                    onClick={showDialog}
                  >
                    Add Vintage
                  </Button>
                </Grid>
            </Grid>

            <DataGrid
              rows={data}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSize={PAGE_SIZE}
              pageSizeOptions={[PAGE_SIZE]}
              disableSelectionOnClick
              disableColumnFilter
              loading={isLoading}
              onRowClick={handleRowClick}
              rowCount={rowCount}
              paginationMode="server"
              sx={{
                height: 52 * PAGE_SIZE + 120,
                border: 'none',
                '& .MuiDataGrid-menuIcon': { display: 'none' },
                '& .MuiDataGrid-columnHeaderTitle': {
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  color: 'rgba(58, 53, 65, 0.87)',
                },
                '& .MuiDataGrid-columnsContainer': {
                  backgroundColor: 'rgb(249, 250, 252)',
                },
                '& .MuiChip-root.pending': {
                  color: 'rgb(102, 60, 0)',
                  backgroundColor: 'rgb(255, 244, 229)',
                },
                '& .MuiChip-root.withdrawn': {
                  color: 'rgb(30, 70, 32)',
                  backgroundColor: 'rgb(237, 247, 237)',
                },
              }}
            />
        </Paper>
        <AddChartwineDialog key={rowId || 'new'} open={dialogVisible} onClose={hideDialog} onSave={onSave} rowId={rowId}  />
      </Container>
    </>
  );
};

export default WineChart;
